import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UploadCatalogoModal = ({ open, onClose, onUploaded, defaultCategory }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(defaultCategory || 'catalogo');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const reset = () => { setFile(null); setTitle(''); setDescription(''); setProgress(0); };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) { toast.error('Selecione um arquivo PDF'); return; }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.pdf$/i, ''));
  };

  const handleSubmit = async () => {
    if (!file || !title.trim()) { toast.error('Selecione o PDF e informe o título'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title.trim());
    fd.append('category', category);
    fd.append('description', description);
    setUploading(true);
    try {
      await axios.post(`${API_URL}/api/catalogos`, fd, {
        onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded * 100) / e.total)),
      });
      toast.success('Arquivo enviado com sucesso');
      reset();
      onUploaded();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar arquivo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !uploading && onClose()}>
      <DialogContent data-testid="catalogo-upload-modal" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Enviar PDF</DialogTitle>
          <DialogDescription>Catálogos e apresentações ficam visíveis para Administradores e Orçamentos.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {[['catalogo', 'Catálogo'], ['apresentacao', 'Apresentação']].map(([val, label]) => (
              <button
                key={val}
                type="button"
                data-testid={`catalogo-category-${val}`}
                onClick={() => setCategory(val)}
                className={`py-2 rounded-md border text-sm font-medium transition-colors ${
                  category === val ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <label
            className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg p-6 cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
          >
            <Upload className="w-6 h-6 text-slate-400" />
            <span className="text-sm text-slate-600 text-center break-all" data-testid="catalogo-file-name">
              {file ? file.name : 'Clique ou arraste um PDF aqui'}
            </span>
            <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="catalogo-file-input"
              onChange={(e) => handleFile(e.target.files[0])} />
          </label>
          <div className="space-y-1.5">
            <Label htmlFor="cat-title">Título</Label>
            <Input id="cat-title" data-testid="catalogo-title-input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cat-desc">Descrição (opcional)</Label>
            <Textarea id="cat-desc" data-testid="catalogo-description-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          {uploading && (
            <div className="w-full bg-slate-100 rounded-full h-2" data-testid="catalogo-upload-progress">
              <div className="bg-blue-600 h-2 rounded-full transition-[width]" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading} data-testid="catalogo-upload-cancel">Cancelar</Button>
          <Button onClick={handleSubmit} disabled={uploading} data-testid="catalogo-upload-submit">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando {progress}%</> : 'Enviar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
